import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  Home,
  LayoutDashboard,
  ListFilter,
  LogOut,
  Menu,
  Settings,
  ShieldCheck,
  UserRound,
  UserRoundSearch,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { dictionary, localizePath, switchLanguagePath } from "@/i18n/translations";

const SidebarItem = ({ item, active, onClick }) => (
  <Link
    to={item.href}
    className={`dashboard-sidebar-item ${active ? "dashboard-sidebar-item-active" : ""}`}
    onClick={onClick}
    data-testid={`dashboard-sidebar-nav-${item.key}`}
  >
    <item.icon size={18} />
    <span>{item.label}</span>
  </Link>
);

const DashboardHomeLink = ({ href, label, testId, onClick }) => (
  <Button
    asChild
    variant="outline"
    size="icon"
    className="rounded-full border-gold-500/30 bg-white/80 text-stone-700 hover:bg-amber-50"
  >
    <Link to={href} aria-label={label} title={label} onClick={onClick} data-testid={testId}>
      <Home size={18} />
      <span className="sr-only">{label}</span>
    </Link>
  </Button>
);

export const DashboardLayout = ({ lang, children }) => {
  const copy = dictionary[lang];
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const nextLang = lang === "ar" ? "en" : "ar";
  const homePath = localizePath(lang);
  const dashboardRoot = localizePath(lang, "/dashboard");

  const navItems = useMemo(() => {
    const items = [
      {
        key: "dashboard",
        label: copy.dashboardNav.dashboard,
        icon: LayoutDashboard,
        href: dashboardRoot,
      },
      {
        key: "properties",
        label: copy.dashboardNav.properties,
        icon: Building2,
        href: localizePath(lang, "/dashboard/properties"),
      },
      {
        key: "add-property",
        label: copy.dashboardNav.addProperty,
        icon: ListFilter,
        href: localizePath(lang, "/dashboard/properties/new"),
      },
      {
        key: "leads",
        label: copy.dashboardNav.leads,
        icon: UserRoundSearch,
        href: localizePath(lang, "/dashboard/leads"),
      },
      {
        key: "settings",
        label: copy.dashboardNav.settings,
        icon: Settings,
        href: localizePath(lang, "/dashboard/settings"),
      },
    ];

    if (user?.role === "admin") {
      items.push({
        key: "admin",
        label: copy.dashboardNav.admin,
        icon: ShieldCheck,
        href: localizePath(lang, "/dashboard/admin"),
      });
    }

    return items;
  }, [copy.dashboardNav, dashboardRoot, lang, user?.role]);

  const isNavItemActive = (href) => {
    if (href === dashboardRoot) {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    logout();
    toast.success(copy.auth.logoutSuccess);
    navigate(localizePath(lang, "/staff/login"));
  };

  const switchLanguage = () => {
    const nextPath = `${switchLanguagePath(location.pathname, nextLang)}${location.search}`;
    navigate(nextPath);
  };

  return (
    <div className="min-h-screen bg-luxury-surface" data-testid="dashboard-layout-root">
      <div className="border-b border-gold-500/20 bg-white/70 backdrop-blur-sm lg:hidden" data-testid="dashboard-layout-mobile-topbar">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4" data-testid="dashboard-layout-mobile-topbar-inner">
          <Link to={dashboardRoot} className="brand-gradient text-lg font-semibold" data-testid="dashboard-layout-mobile-brand-link">
            {copy.nav.brand}
          </Link>

          <div className="flex items-center gap-2" data-testid="dashboard-layout-mobile-topbar-actions">
            <DashboardHomeLink
              href={homePath}
              label={copy.nav.home}
              testId="dashboard-layout-mobile-home-link"
            />

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" data-testid="dashboard-layout-mobile-hamburger-button">
                  <Menu size={18} />
                </Button>
              </SheetTrigger>

              <SheetContent side="right" className="w-[86vw] max-w-[320px] border-l border-gold-500/20 bg-[#f7efdf]" data-testid="dashboard-layout-mobile-drawer">
                <SheetHeader>
                  <SheetTitle data-testid="dashboard-layout-mobile-drawer-title">{copy.nav.dashboard}</SheetTitle>
                  <SheetDescription className="sr-only" data-testid="dashboard-layout-mobile-drawer-description">
                    {copy.dashboardLayout.drawerDescription}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-2" data-testid="dashboard-layout-mobile-drawer-nav">
                  {navItems.map((item) => (
                    <SidebarItem
                      key={item.key}
                      item={item}
                      active={isNavItemActive(item.href)}
                      onClick={() => setMobileMenuOpen(false)}
                    />
                  ))}
                </div>

                <div className="mt-8 space-y-2" data-testid="dashboard-layout-mobile-drawer-actions">
                  <Button className="w-full justify-start" variant="outline" onClick={switchLanguage} data-testid="dashboard-layout-mobile-language-button">
                    {copy.nav.switchTo}
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={handleLogout} data-testid="dashboard-layout-mobile-logout-button">
                    <LogOut size={16} className="me-2" />
                    {copy.nav.logout}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <aside className="dashboard-sidebar hidden lg:flex" data-testid="dashboard-layout-desktop-sidebar">
        <div className="flex h-full flex-col" data-testid="dashboard-layout-desktop-sidebar-content">
          <div className="flex items-center justify-between gap-3" data-testid="dashboard-layout-desktop-brand-row">
            <Link to={dashboardRoot} className="brand-gradient text-2xl font-semibold" data-testid="dashboard-layout-desktop-brand-link">
              {copy.nav.brand}
            </Link>
            <DashboardHomeLink
              href={homePath}
              label={copy.nav.home}
              testId="dashboard-layout-desktop-home-link"
            />
          </div>

          <div className="mt-6 rounded-2xl border border-gold-500/20 bg-white/70 p-4" data-testid="dashboard-layout-user-box">
            <p className="text-xs text-stone-500" data-testid="dashboard-layout-user-role">
              {user?.role === "admin" ? copy.dashboard.roleAdmin : copy.dashboard.roleStaff}
            </p>
            <p className="mt-1 text-sm font-semibold text-stone-800" data-testid="dashboard-layout-user-email">
              {user?.email}
            </p>
          </div>

          <nav className="mt-6 space-y-2" data-testid="dashboard-layout-desktop-nav">
            {navItems.map((item) => (
              <SidebarItem
                key={item.key}
                item={item}
                active={isNavItemActive(item.href)}
              />
            ))}
          </nav>

          <div className="mt-auto space-y-2" data-testid="dashboard-layout-desktop-actions">
            <Button className="w-full justify-start" variant="outline" onClick={switchLanguage} data-testid="dashboard-layout-desktop-language-button">
              {copy.nav.switchTo}
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={handleLogout} data-testid="dashboard-layout-desktop-logout-button">
              <LogOut size={16} className="me-2" />
              {copy.nav.logout}
            </Button>
          </div>
        </div>
      </aside>

      <main className="dashboard-main" data-testid="dashboard-layout-main-content">
        <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-7 lg:py-8" data-testid="dashboard-layout-main-inner">
          {children}
        </div>
      </main>
    </div>
  );
};
